import React, { useState, useEffect, useRef } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  CircularProgress,
  IconButton,
  Switch,
  Modal,
  Box,
  Button,
} from "@mui/material";
import { axiosInstance } from "../../axiosInstance";
import { FaEye } from "react-icons/fa";
import { GoSearch } from "react-icons/go";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

const columns = [
  { id: "name", label: "NAME", minWidth: 150, align: "center" },
  { id: "user_id", label: "USER ID", minWidth: 150, align: "center" },
  {
    id: "referral_code",
    label: "REFERRAL CODE",
    minWidth: 150,
    align: "center",
  },
  {
    id: "phone_number",
    label: "PHONE NUMBER",
    minWidth: 150,
    align: "center",
  },
  {
    id: "actions",
    label: "ACTIONS",
    minWidth: 150,
    align: "center",
  },
];

export function ReferralAgents() {
  const [error, setError] = useState("");
  const [ReferralAgentsList, setReferralAgentsList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [toggleModalOpen, setToggleModalOpen] = useState(false);
  const [toggleTarget, setToggleTarget] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  // const [page, setPage] = useState(0);
  // const [rowsPerPage, setRowsPerPage] = useState(20);

  // const handleChangePage = (event, newPage) => {
  //   setPage(newPage);
  // };

  // const handleChangeRowsPerPage = (event) => {
  //   setRowsPerPage(+event.target.value);
  //   setPage(0);
  // };

  const fetchReferralAgents = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get("/admin/agent-profile/");
      console.log("✅ agent profile list response:", res.data);
      if (res.data.success) {
        const mappedData = (res.data.data || []).map((item) => ({
          id: item.id,
          name: `${item.first_name || ""} ${item.last_name || ""}`.trim(),
          user_id: item.id,
          referral_code: item.user_referal_code?.code || "--",
          phone_number: item.mobile_no,
          is_active: item.is_active,
        }));
        setReferralAgentsList(mappedData);
      } else {
        setError("Failed to fetch agent profile data.");
      }
    } catch (err) {
      console.error("❌ Error fetching agent profile data:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferralAgents();
  }, []);

  const handleToggleStatus = async () => {
    try {
      const res = await axiosInstance.patch(
        `/admin/agent-profile/${toggleTarget.id}/`,
        {
          is_active: !toggleTarget.is_active,
        }
      );
      if (res.data.success) {
        toast.success("Status updated successfully");
        fetchReferralAgents();
        setToggleModalOpen(false);
      } else {
        toast.error("Failed to update status");
      }
    } catch (err) {
      toast.error("Error updating status");
    }
  };

  //
  //  *************** DATE FILTERS (FIXED) ***************
  //
  const [filterDate, setFilterDate] = useState(null);

  // APPLY FILTER
  const applyDateFilters = async (selectedDate) => {
    if (!selectedDate) return;

    setLoading(true);
    setError("");

    try {
      const formattedDate = dayjs(selectedDate).format("DD-MM-YYYY");

      const res = await axiosInstance.get("/admin/agent-profile/", {
        params: { date: formattedDate },
      });

      if (res.data.success) {
        const mappedData = (res.data.data || []).map((item) => ({
          id: item.id,
          name: `${item.first_name || ""} ${item.last_name || ""}`.trim(),
          user_id: item.id,
          referral_code: item.user_referal_code?.code || "--",
          phone_number: item.mobile_no,
          is_active: item.is_active,
        }));
        setReferralAgentsList(mappedData);
      } else {
        setError("Filter failed.");
      }
    } catch (err) {
      console.error(err);
      setError("Something went wrong.");
    } finally {
      setLoading(false);
      setPage(0);
    }
  };

  // CLEAR DATE
  const clearDateFilters = () => {
    setFilterDate(null);
    fetchReferralAgents();
  };

  useEffect(() => {
    fetchReferralAgents();
  }, []);

  // Search filter (by name or phone number)
  const filteredData = ReferralAgentsList.filter(
    (row) =>
      row.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.phone_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.referral_code.toLowerCase().includes(searchQuery.toLowerCase())
  );
  // save table page size in local to save data on page change
  const [page, setPage] = useState(() => {
    const savedPage = localStorage.getItem("referral_page");
    return savedPage ? Number(savedPage) : 0;
  });

  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const savedRows = localStorage.getItem("referral_rowsPerPage");
    return savedRows ? Number(savedRows) : 20;
  });
  const pageChangedByUser = useRef(false);

  const handleChangePage = (event, newPage) => {
    pageChangedByUser.current = true;
    setPage(newPage);
    localStorage.setItem("referral_page", newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const value = Number(event.target.value);
    pageChangedByUser.current = true;

    setRowsPerPage(value);
    setPage(0);

    localStorage.setItem("referral_rowsPerPage", value);
    localStorage.setItem("referral_page", 0);
  };

  // Reset page on search and filters

  useEffect(() => {
    if (loading) return;

    // Do NOT reset page on refresh
    if (!pageChangedByUser.current) return;

    const maxPage = Math.max(
      0,
      Math.ceil(filteredData.length / rowsPerPage) - 1
    );

    if (page > maxPage) {
      setPage(0);
      localStorage.setItem("referral_page", 0);
    }
  }, [filteredData.length, rowsPerPage, loading, page]);
  return (
    <>
      <div>
        <div className="admin-top-container">
          <div className="admin-listText">Referral Agents</div>
          {/* <div>
            <select
              className="listAdmin-showRecords"
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
            >
              <option value={20}>Show No of Records</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
              <option value={1000}>1000</option>
              <option value={2000}>2000</option>
              <option value={5000}>5000</option>
            </select>
          </div> */}
          <div>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Select Date"
                value={filterDate}
                format="DD-MM-YYYY"
                maxDate={dayjs()}
                onChange={(newValue) => {
                  setFilterDate(newValue);
                  applyDateFilters(newValue);
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        height: 35,
                        width: 180,
                        "& fieldset": {
                          border: "1px solid #b8b9b9", // custom border
                        },
                        "&:hover fieldset": {
                          border: "1px solid #b8b9b9", // hover border
                        },
                        "&.Mui-focused fieldset": {
                          border: "1px solid #b8b9b9", // focus border
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "black", // label color
                        fontSize: "0.85rem",
                      },
                      "& .MuiInputBase-input": {
                        padding: "4px 8px", // adjust padding
                        fontSize: "0.85rem",
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </div>
          <div className="search-container">
            <div className="nav-search">
              <span className="search-icons">
                <GoSearch />
              </span>
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ border: "none", outline: "none", marginLeft: "6px" }}
              />
            </div>
          </div>
        </div>

        <div>
          <button className="reset-btn" onClick={clearDateFilters}>
            Reset
          </button>
        </div>

        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <TableContainer sx={{ maxHeight: 440 }} className="Table-container">
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                      className="table-header"
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredData.length > 0 ? (
                  filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => (
                      <TableRow
                        className="table-row"
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={index}
                        sx={{
                          backgroundColor:
                            index % 2 === 1 ? "#E7F0F9" : "white",
                        }}
                      >
                        {columns.map((column) => {
                          const value = row[column.id];
                          return (
                            <TableCell
                              key={column.id}
                              align={column.align}
                              className={`${column.id}-column tableCelldata`}
                              sx={{
                                color:
                                  column.id === "status"
                                    ? value === "Active"
                                      ? "green"
                                      : "red"
                                    : "inherit",
                              }}
                            >
                              {column.id === "actions" ? (
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    gap: "8px",
                                  }}
                                >
                                  <IconButton sx={{ color: "black" }}>
                                    <FaEye
                                      style={{
                                        cursor: "pointer",
                                        fontSize: "18px",
                                      }}
                                      onClick={() =>
                                        navigate(
                                          `/referral-agents-detailes/${row.id}`
                                        )
                                      }
                                    />
                                  </IconButton>
                                  <div
                                    className="form-check form-switch"
                                    style={{ marginTop: "5px" }}
                                  >
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      id={`toggleSwitch-${row.id}`}
                                      checked={row.is_active}
                                      onChange={() => {
                                        setToggleTarget(row);
                                        setToggleModalOpen(true);
                                      }}
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor={`toggleSwitch-${row.id}`}
                                    ></label>
                                  </div>
                                </div>
                              ) : value === null ||
                                value === undefined ||
                                value === "" ? (
                                "--"
                              ) : (
                                value
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      align="center"
                      style={{ padding: "40px 0" }}
                    >
                      No Data Available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[20, 50, 100, 500, 1000, 2000, 5000]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </div>

      {/* Active or InActive Confirmation Modal */}

      <Modal
        open={toggleModalOpen}
        onClose={() => setToggleModalOpen(false)}
        aria-labelledby="toggle-status-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <h3 id="toggle-status-title">Are you sure?</h3>
          <hr />
          <p>
            Do you want to {toggleTarget?.is_active ? "INACTIVATE" : "ACTIVATE"}{" "}
            this Agent?
          </p>
          <hr />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <Button
              onClick={() => setToggleModalOpen(false)}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleToggleStatus}
              variant="contained"
              color="primary"
            >
              Confirm
            </Button>
          </div>
        </Box>
      </Modal>
    </>
  );
}
